import api from '../services/api';
import DuprSessionPanel from '../components/dupr/DuprSessionPanel';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  Trophy,
  Pencil,
  UserX,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import { formatCurrency, formatDate } from '../utils/formatters';

function formatTime(value) {
  if (!value) return 'N/A';
  const [hours, minutes] = String(value).split(':').map(Number);
  if (!Number.isFinite(hours)) return value;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  return `${hours % 12 || 12}:${String(minutes || 0).padStart(2, '0')} ${suffix}`;
}

export default function OpenPlayDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [openPlay, setOpenPlay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removingPlayerId, setRemovingPlayerId] = useState(null);

  const loadOpenPlay = useCallback(async () => {
    setLoading(true);
    try {
      setOpenPlay(await adminService.getOpenPlay(id));
    } catch (error) {
      setOpenPlay(null);
      toast.error(error.response?.data?.message || 'Failed to load Open Play details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOpenPlay();
  }, [loadOpenPlay]);

  async function removePlayer(player) {
    if (!window.confirm(`Remove “${player.name}” from this Open Play?`)) return;

    setRemovingPlayerId(player.playerId);
    try {
      await adminService.removeOpenPlayPlayer(id, player.playerId);
      setOpenPlay((current) => ({
        ...current,
        playerCount: Math.max(0, current.playerCount - 1),
        players: current.players.filter((item) => item.playerId !== player.playerId),
      }));
      toast.success('Player removed from Open Play');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove player');
    } finally {
      setRemovingPlayerId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
      </div>
    );
  }

  if (!openPlay) {
    return (
      <div className="admin-card py-16 text-center">
        <Trophy className="mx-auto h-12 w-12 text-[#94A3B8]" />
        <h1 className="mt-4 font-semibold text-[#1F2937]">Open Play not found</h1>
        <button type="button" onClick={() => navigate('/openplays')} className="admin-btn-outline mt-5 !h-11">Back to Open Play</button>
      </div>
    );
  }

  const summary = [
    { label: 'Venue', value: openPlay.venueName, helper: [openPlay.address, openPlay.city].filter(Boolean).join(', '), icon: MapPin },
    { label: 'Date', value: formatDate(openPlay.date), icon: Calendar },
    { label: 'Time', value: `${formatTime(openPlay.startTime)}–${formatTime(openPlay.endTime)}`, icon: Clock },
    { label: 'Players', value: `${openPlay.playerCount} / ${openPlay.maxPlayers}`, icon: Users },
  ];

  return (
    <div className="space-y-6">
      <DuprSessionPanel api={api} openPlayId={id} />
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => navigate('/openplays')} className="admin-btn-outline !h-11 !rounded-xl !px-4">
          <ArrowLeft size={17} className="mr-2" /> Back
        </button>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-[#1F2937]">{openPlay.name}</h1>
          <p className="mt-1 text-sm text-[#667085]">Open Play / tournament details</p>
        </div>
        {openPlay.status === 'Active' && (
          <button type="button" onClick={() => navigate(`/openplays/${id}/edit`)} className="admin-btn-outline ml-auto !h-11 !rounded-xl !px-4">
            <Pencil size={16} className="mr-2" /> Edit
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summary.map((item) => (
          <div key={item.label} className="admin-card !p-5">
            <div className="mb-2 flex items-center gap-2 text-[#667085]">
              <item.icon size={17} />
              <span className="text-xs font-semibold uppercase tracking-wide">{item.label}</span>
            </div>
            <p className="font-semibold text-[#1F2937]">{item.value}</p>
            {item.helper && <p className="mt-1 text-xs text-[#667085]">{item.helper}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="admin-card lg:col-span-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#667085]">Description</h2>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[#1F2937]">
            {openPlay.description || 'No description was provided.'}
          </p>
        </div>
        <div className="admin-card">
          <div className="flex items-center gap-2 text-[#667085]">
            <DollarSign size={17} />
            <h2 className="text-sm font-semibold uppercase tracking-wide">Price per player</h2>
          </div>
          <p className="mt-3 text-2xl font-bold text-[#264D59]">{formatCurrency(openPlay.pricePerPlayer)}</p>
          {openPlay.courtNames?.length > 0 && (
            <div className="mt-5 border-t border-[#E2E8F0] pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#667085]">Reserved courts</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {openPlay.courtNames.map((name) => <span key={name} className="admin-badge-neutral">{name}</span>)}
              </div>
            </div>
          )}
        </div>
      </div>

      <section className="admin-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-[#1F2937]">Registered players</h2>
            <p className="mt-1 text-xs text-[#667085]">{openPlay.playerCount} confirmed registration{openPlay.playerCount === 1 ? '' : 's'}</p>
          </div>
          <Users className="text-[#56B7AC]" size={22} />
        </div>

        {openPlay.players?.length ? (
          <div className="mt-5 divide-y divide-[#E2E8F0]">
            {openPlay.players.map((player) => (
              <div key={player.playerId} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E0F7F4] text-sm font-bold text-[#264D59]">
                    {(player.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-[#1F2937]">{player.name}</p>
                    <p className="text-xs text-[#667085]">
                      {player.skillLevel || 'Skill level not set'} · Joined {formatDate(player.joinedAt)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removePlayer(player)}
                  disabled={Boolean(removingPlayerId)}
                  className="inline-flex h-9 items-center rounded-xl px-3 text-sm font-semibold text-[#D46C4E] transition-colors hover:bg-[#FDF1EB] disabled:opacity-50"
                >
                  <UserX size={16} className="mr-2" />
                  {removingPlayerId === player.playerId ? 'Removing…' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-5 rounded-xl bg-[#F8FAFC] py-10 text-center">
            <Users className="mx-auto h-8 w-8 text-[#94A3B8]" />
            <p className="mt-2 text-sm text-[#667085]">No confirmed players yet</p>
          </div>
        )}
      </section>
    </div>
  );
}
